import React, { useCallback, useContext, useRef, useState } from 'react';
import { Copy } from 'assets/svg';
import { toBlob } from 'html-to-image';
import { Modal, Spin, Popover } from 'antd';
import styled from '@emotion/styled';
import { blobToBase64, calculateFontSize, getAdditionalDataOnCopyClick, getConfig } from 'utils/helperMethods';
import { FilterContextData } from 'contexts/FilterContext';
import { UserContext } from 'contexts/ConfigContext';

interface Props {
    refOfComponent: React.RefObject<HTMLElement>;
    mixpanelEvent?: Function;
    removeScrollbar?: boolean;
    elementClassToRemoveScrollbar?: string;
    extraData?: Record<string, string[]>;
}

const PopoverStyled = styled(Popover)`
    .ant-popover-inner-content {
        padding: 7px 14px;
    }
`;
export const classToRemoveScrollbar = 'copy-widget-remove-scrollbar';

const popUpWidth = calculateFontSize(window.innerWidth, 600);

function CopyToClipBoard({
    refOfComponent,
    mixpanelEvent = undefined,
    removeScrollbar = false,
    elementClassToRemoveScrollbar,
    extraData,
}: Props) {
    const { allCheckedAttribute, timeSelectionLabel, timePeriodSelection } = useContext(FilterContextData);
    const { appConfig } = useContext(UserContext);
    const commonConfig = getConfig('commonConfig', appConfig);
    const ModalStyled = styled(Modal)`
        .ant-modal-content {
            border-radius: 10px;
        }
    `;
    const [open, setOpen] = useState(false);
    const [showCopy, setShowCopy] = useState(false);

    const imageRef = useRef<any>();
    const additionalDataRef = useRef<any>();
    const onButtonClick = () => {
        setOpen(true);
        //mixpanel event
        if (mixpanelEvent) mixpanelEvent();
        const additionalData = getAdditionalDataOnCopyClick(
            allCheckedAttribute,
            timeSelectionLabel,
            timePeriodSelection,
            commonConfig,
            extraData,
        );
        additionalDataRef.current = additionalData ? additionalData : '';
        if (refOfComponent.current === null) {
            return;
        }
        const className = elementClassToRemoveScrollbar ?? classToRemoveScrollbar;
        let scrollbarHideElement: HTMLCollectionOf<HTMLElement> | null = null;
        let cssText = '';

        //if remove scrollbar is true
        //find the element, make overflow as hidden
        //and once blob is created, revert overflow back
        if (removeScrollbar) {
            scrollbarHideElement = refOfComponent.current.getElementsByClassName(
                className,
            ) as HTMLCollectionOf<HTMLElement>;

            if (scrollbarHideElement && scrollbarHideElement?.length > 0) {
                cssText = scrollbarHideElement?.[0]?.style.cssText;
                scrollbarHideElement[0].style.overflow = 'hidden';
            }
        }

        //  temp.style.scrollbar = 'hidden';
        toBlob(refOfComponent.current, {
            backgroundColor: '#FFFFFF',
            height: refOfComponent.current?.clientHeight,
        })
            .then(async (imgBlob: any) => {
                //and once blob is created, revert overflow back
                if (removeScrollbar && scrollbarHideElement && scrollbarHideElement?.length > 0) {
                    scrollbarHideElement[0].style.cssText = cssText;
                }
                //converting blob to base 64
                const imageBase64Data = await blobToBase64(imgBlob);
                imageRef.current = imageBase64Data;
                //html data
                const html = `<div><img src="${imageBase64Data}" alt="Some image"></div>
            ${additionalData}
            `;

                //html blob
                const htmlBlob = new Blob([html], {
                    type: 'text/html',
                });

                //adding to clipboard
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': htmlBlob,
                    }),
                ]);
                setTimeout(() => setShowCopy(true), 300);
            })
            .catch((error) => setOpen(false));
        // setTimeout(() => setShowCopy(true), 1300);
    };

    function onCancel() {
        imageRef.current = null;
        additionalDataRef.current = null;
        setOpen(false);
        setShowCopy(false);
    }

    return (
        <>
            <PopoverStyled
                placement="bottom"
                trigger="hover"
                showArrow={false}
                content={<p className="font-xxs">Copy</p>}
                overlayInnerStyle={{ position: 'relative', top: -10, borderRadius: '5px', padding: '0px' }}
            >
                <div onClick={onButtonClick} className="h-fit">
                    <Copy />
                </div>
            </PopoverStyled>
            <ModalStyled
                centered
                destroyOnClose
                open={open}
                footer={''}
                style={{ resize: 'none', borderRadius: '200px' }}
                width={popUpWidth}
                onCancel={() => {
                    onCancel();
                }}
            >
                <div className="font-xs font-medium">Preparing your copy</div>
                {showCopy ? (
                    <>
                        <img src={imageRef.current} style={{ width: popUpWidth, marginTop: 10 }} />
                        <div
                            style={{ maxHeight: 120, overflowY: 'auto', marginBlock: 10 }}
                            dangerouslySetInnerHTML={{ __html: additionalDataRef.current }}
                        />
                        <div className="w-full flex justify-end mt-8" onClick={() => onCancel()}>
                            <div className="w-fit font-xs min-h-[2.5rem] px-3 py-2 flex bg-purpleFont items-center rounded cursor-pointer text-white">
                                Copy to Clipboard
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex justify-center items-center mt-8">
                        <Spin />
                    </div>
                )}
            </ModalStyled>
        </>
    );
}

export default CopyToClipBoard;
